import React, { useState } from 'react';
import { Card } from '../Shared/Card';
import { FileTextIcon, DownloadIcon, SaveIcon, AlertTriangleIcon, CheckIcon, XIcon, PlusIcon, FilterIcon, ArrowRightIcon, EyeIcon, GitBranchIcon, ClockIcon, InfoIcon, RefreshCwIcon, SendIcon, DatabaseIcon, ChevronDownIcon, ChevronRightIcon, SettingsIcon, TableIcon, FileIcon } from 'lucide-react';
import { Loan } from '../../types/LoanTypes';
type FieldStatus = 'ready' | 'warning' | 'missing';
type DeliveryMethod = 'download' | 'sftp' | 'email' | 'api';
type ExportFormat = 'xlsx' | 'csv' | 'json' | 'pdf';
interface ExportField {
  id: string;
  exportName: string;
  sourceField: string;
  transformation: string | null;
  status: FieldStatus;
  warning?: string;
}
interface ExportTemplate {
  id: string;
  name: string;
  counterparty: string;
  poolName: string;
  format: ExportFormat;
  deliveryMethod: DeliveryMethod;
  versioningEnabled: boolean;
  fields: ExportField[];
  createdBy: string;
  createdDate: string;
  lastModified: string;
  targetSystem: string;
}
export function ExportBuilder() {
  const [activeTemplate, setActiveTemplate] = useState<ExportTemplate>(mockTemplates[0]);
  const [selectedTab, setSelectedTab] = useState<'fields' | 'preview' | 'history'>('fields');
  const [expandedSection, setExpandedSection] = useState<string | null>('preview');
  const [showFieldMapping, setShowFieldMapping] = useState(false);
  const [showTemplateMetadata, setShowTemplateMetadata] = useState(true);
  // Loans data for preview
  const previewLoans: Loan[] = [{
    id: '#005367',
    borrower: 'David Lee',
    type: 'DSCR',
    loanProgram: 'DSCR',
    amount: '$650,000',
    fico: 695,
    ltv: '75%',
    dscr: '1.25',
    status: 'Performing',
    state: 'FL',
    eligibleForDBRS: false
  }, {
    id: '#005389',
    borrower: 'Jennifer Adams',
    type: 'DSCR',
    loanProgram: 'DSCR',
    amount: '$920,000',
    fico: 735,
    ltv: '65%',
    dscr: '1.30',
    status: 'Performing',
    state: 'CA',
    eligibleForDBRS: false
  }, {
    id: '#005312',
    borrower: 'Alex Young',
    type: 'Fix-and-flip',
    loanProgram: 'RTL',
    amount: '$500,000',
    fico: 720,
    ltv: '67%',
    dscr: '1.25',
    status: 'Performing',
    state: 'NY',
    eligibleForDBRS: true
  }, {
    id: '#005324',
    borrower: 'Maria Chen',
    type: 'DSCR',
    loanProgram: 'DSCR',
    amount: '$750,000',
    fico: 745,
    ltv: '72%',
    dscr: '1.15',
    status: 'Performing',
    state: 'CA',
    eligibleForDBRS: false
  }, {
    id: '#005355',
    borrower: 'Robert Johnson',
    type: 'DSCR',
    loanProgram: 'DSCR',
    amount: '$850,000',
    fico: 735,
    ltv: '68%',
    dscr: '1.35',
    status: 'Performing',
    state: 'IL',
    eligibleForDBRS: false
  }];
  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };
  const handleAddField = () => {
    setShowFieldMapping(true);
  };
  const handleCloseFieldMapping = () => {
    setShowFieldMapping(false);
  };
  const handleSaveTemplate = () => {
    alert('Template saved successfully!');
  };
  const handleRunExport = () => {
    alert('Export generated successfully! Ready for download.');
  };
  const handleSubmitToBuyer = () => {
    alert('Export submitted to buyer via SFTP!');
  };
  const getStatusIcon = (status: FieldStatus) => {
    switch (status) {
      case 'ready':
        return <CheckIcon className="w-4 h-4 text-green-500" data-id="element-705" />;
      case 'warning':
        return <AlertTriangleIcon className="w-4 h-4 text-amber-500" data-id="element-706" />;
      case 'missing':
        return <XIcon className="w-4 h-4 text-red-500" data-id="element-707" />;
    }
  };
  const getStatusText = (status: FieldStatus) => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'warning':
        return 'Warning';
      case 'missing':
        return 'Missing';
    }
  };
  const getStatusClass = (status: FieldStatus) => {
    switch (status) {
      case 'ready':
        return 'text-green-600';
      case 'warning':
        return 'text-amber-600';
      case 'missing':
        return 'text-red-600';
    }
  };
  const formatFieldValue = (loan: Loan, field: ExportField) => {
    // This is a simplified implementation - in a real app, this would use the actual field paths
    switch (field.sourceField) {
      case 'loan.id':
        return loan.id.replace('#', '');
      case 'borrower.fullName':
        // Apply transformation if needed
        if (field.transformation === 'Mask Last Name') {
          const nameParts = loan.borrower.split(' ');
          return `${nameParts[0]} ${nameParts[1].charAt(0)}.`;
        }
        return loan.borrower;
      case 'property.value':
        // Apply transformation if needed
        if (field.transformation === 'Rounded to $1,000') {
          return loan.amount;
        }
        return loan.amount;
      case 'fico.score':
        return loan.fico.toString();
      case 'loan.ltv':
        return loan.ltv;
      case 'underwriting.dscr':
        // Apply transformation if needed
        if (field.transformation === '2 decimal places') {
          return parseFloat(loan.dscr).toFixed(2);
        }
        return loan.dscr;
      case 'documents.appraisal.date':
        // This field is missing in our data model
        return '—';
      default:
        return '—';
    }
  };
  return <div className="space-y-6" data-id="element-708">
      <div className="flex justify-between items-center" data-id="element-709">
        <h2 className="text-xl font-semibold text-gray-800" data-id="element-710">Export Builder</h2>
        <div className="flex space-x-3" data-id="element-711">
          <button className="px-4 py-2 bg-background border border-border rounded-full text-foreground/70 hover:bg-muted flex items-center text-sm transition-colors" onClick={handleSaveTemplate} data-id="element-712">
            <SaveIcon className="w-4 h-4 mr-2" data-id="element-713" />
            Save Template
          </button>
          <button className="px-4 py-2 bg-success rounded-full text-foreground/70 hover:bg-success/80 flex items-center text-sm transition-colors" onClick={handleRunExport} data-id="element-714">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-715" />
            Run Export
          </button>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-md flex items-start" data-id="element-716">
        <InfoIcon className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-717" />
        <div data-id="element-718">
          <h3 className="font-medium text-blue-800" data-id="element-719">About Export Builder</h3>
          <p className="text-sm text-blue-700 mt-1" data-id="element-720">
            Configure and generate export packages for counterparties, rating
            agencies, and internal systems. Map fields, apply transformations,
            and validate data before exporting.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-721">
        <div className="lg:col-span-3 space-y-6" data-id="element-722">
          {/* Export Configuration Header */}
          <Card data-id="element-723">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-724">
              Export Configuration
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-725">
              <div data-id="element-726">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-727">
                  Export Package Name
                </label>
                <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={activeTemplate.name} onChange={e => setActiveTemplate({
                ...activeTemplate,
                name: e.target.value
              })} data-id="element-728" />
              </div>
              <div data-id="element-729">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-730">
                  Buyer / Counterparty
                </label>
                <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={activeTemplate.counterparty} onChange={e => setActiveTemplate({
                ...activeTemplate,
                counterparty: e.target.value
              })} data-id="element-731" />
              </div>
              <div data-id="element-732">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-733">
                  Pool Name
                </label>
                <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={activeTemplate.poolName} onChange={e => setActiveTemplate({
                ...activeTemplate,
                poolName: e.target.value
              })} data-id="element-734" />
              </div>
              <div data-id="element-735">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-736">
                  Export Format
                </label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2" value={activeTemplate.format} onChange={e => setActiveTemplate({
                ...activeTemplate,
                format: e.target.value as ExportFormat
              })} data-id="element-737">
                  <option value="xlsx" data-id="element-738">.XLSX</option>
                  <option value="csv" data-id="element-739">.CSV</option>
                  <option value="json" data-id="element-740">.JSON</option>
                  <option value="pdf" data-id="element-741">.PDF</option>
                </select>
              </div>
              <div data-id="element-742">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-743">
                  Delivery Method
                </label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2" value={activeTemplate.deliveryMethod} onChange={e => setActiveTemplate({
                ...activeTemplate,
                deliveryMethod: e.target.value as DeliveryMethod
              })} data-id="element-744">
                  <option value="download" data-id="element-745">Download Now</option>
                  <option value="sftp" data-id="element-746">SFTP</option>
                  <option value="email" data-id="element-747">Email</option>
                  <option value="api" data-id="element-748">API</option>
                </select>
              </div>
              <div className="flex items-center pt-7" data-id="element-749">
                <label className="flex items-center text-sm text-gray-700" data-id="element-750">
                  <input type="checkbox" className="mr-2 h-4 w-4 text-blue-600 rounded" checked={activeTemplate.versioningEnabled} onChange={e => setActiveTemplate({
                  ...activeTemplate,
                  versioningEnabled: e.target.checked
                })} data-id="element-751" />
                  Versioning Enabled
                </label>
              </div>
            </div>
          </Card>
          {/* Tabs for Field Selector, Preview, and History */}
          <div className="border-b border-gray-200" data-id="element-752">
            <nav className="-mb-px flex space-x-6" data-id="element-753">
              <button onClick={() => setSelectedTab('fields')} className={`py-2 px-1 border-b-2 font-medium text-sm ${selectedTab === 'fields' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-754">
                Field Selector & Mapping
              </button>
              <button onClick={() => setSelectedTab('preview')} className={`py-2 px-1 border-b-2 font-medium text-sm ${selectedTab === 'preview' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-755">
                Preview Export Data
              </button>
              <button onClick={() => setSelectedTab('history')} className={`py-2 px-1 border-b-2 font-medium text-sm ${selectedTab === 'history' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-756">
                Export History
              </button>
            </nav>
          </div>
          {/* Field Selector & Mapping Table */}
          {selectedTab === 'fields' && <Card data-id="element-757">
              <div className="flex justify-between items-center mb-4" data-id="element-758">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-759">
                  Field Mapping
                </h3>
                <button className="px-3 py-1.5 bg-blue-600 rounded-md text-white text-sm hover:bg-blue-700 flex items-center" onClick={handleAddField} data-id="element-760">
                  <PlusIcon className="w-4 h-4 mr-1.5" data-id="element-761" />
                  Add Field
                </button>
              </div>
              <div className="overflow-x-auto" data-id="element-762">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-763">
                  <thead className="bg-gray-50" data-id="element-764">
                    <tr data-id="element-765">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-766">
                        Export Field Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-767">
                        Source Field
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-768">
                        Transformation
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-769">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-770">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-771">
                    {activeTemplate.fields.map(field => <tr key={field.id} className="hover:bg-gray-50" data-id="element-772">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-773">
                          {field.exportName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-774">
                          {field.sourceField}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-775">
                          {field.transformation || 'None'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-776">
                          <div className="flex items-center" data-id="element-777">
                            {getStatusIcon(field.status)}
                            <span className={`ml-1.5 text-sm ${getStatusClass(field.status)}`} data-id="element-778">
                              {getStatusText(field.status)}
                            </span>
                            {field.warning && <span className="ml-2 text-xs text-amber-600" data-id="element-779">
                                ({field.warning})
                              </span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-780">
                          <div className="flex space-x-3" data-id="element-781">
                            <button className="text-blue-600 hover:text-blue-800 flex items-center text-sm" data-id="element-782">
                              <SettingsIcon className="w-3.5 h-3.5 mr-1" data-id="element-783" />
                              Map
                            </button>
                            <button className="text-blue-600 hover:text-blue-800 flex items-center text-sm" data-id="element-784">
                              <ArrowRightIcon className="w-3.5 h-3.5 mr-1" data-id="element-785" />
                              Transform
                            </button>
                            <button className="text-blue-600 hover:text-blue-800 flex items-center text-sm" data-id="element-786">
                              <GitBranchIcon className="w-3.5 h-3.5 mr-1" data-id="element-787" />
                              Lineage
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-788">
                <div className="flex items-center justify-between" data-id="element-789">
                  <div className="flex items-center space-x-4" data-id="element-790">
                    <div className="flex items-center" data-id="element-791">
                      <div className="h-3 w-3 rounded-full bg-green-500 mr-1.5" data-id="element-792"></div>
                      <span className="text-xs text-gray-600" data-id="element-793">Ready</span>
                    </div>
                    <div className="flex items-center" data-id="element-794">
                      <div className="h-3 w-3 rounded-full bg-amber-500 mr-1.5" data-id="element-795"></div>
                      <span className="text-xs text-gray-600" data-id="element-796">Warning</span>
                    </div>
                    <div className="flex items-center" data-id="element-797">
                      <div className="h-3 w-3 rounded-full bg-red-500 mr-1.5" data-id="element-798"></div>
                      <span className="text-xs text-gray-600" data-id="element-799">Missing</span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-800">
                    {activeTemplate.fields.filter(f => f.status === 'ready').length}{' '}
                    of {activeTemplate.fields.length} fields ready
                  </div>
                </div>
              </div>
            </Card>}
          {/* Preview Export Data */}
          {selectedTab === 'preview' && <Card data-id="element-801">
              <div className="flex justify-between items-center mb-4" data-id="element-802">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-803">
                  Preview Export Data
                </h3>
                <div className="flex items-center space-x-2" data-id="element-804">
                  <span className="text-sm text-gray-500" data-id="element-805">
                    Showing 5 of 42 loans
                  </span>
                  <button className="px-2 py-1 bg-gray-100 rounded text-gray-700 text-sm hover:bg-gray-200" data-id="element-806">
                    <RefreshCwIcon className="w-3.5 h-3.5" data-id="element-807" />
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-808">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-809">
                  <thead className="bg-gray-50" data-id="element-810">
                    <tr data-id="element-811">
                      {activeTemplate.fields.map(field => <th key={field.id} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-812">
                          {field.exportName}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-813">
                    {previewLoans.map(loan => <tr key={loan.id} className="hover:bg-gray-50" data-id="element-814">
                        {activeTemplate.fields.map(field => <td key={`${loan.id}-${field.id}`} className={`px-6 py-4 whitespace-nowrap text-sm ${field.status === 'missing' ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-815">
                            {formatFieldValue(loan, field)}
                          </td>)}
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3" data-id="element-816">
                <div className="flex items-start" data-id="element-817">
                  <InfoIcon className="w-4 h-4 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" data-id="element-818" />
                  <div data-id="element-819">
                    <p className="text-xs font-medium text-blue-800" data-id="element-820">
                      Preview Mode
                    </p>
                    <p className="text-xs text-blue-700 mt-0.5" data-id="element-821">
                      This is a preview of how your export will appear. Some
                      fields may be missing or contain warnings. Run the export
                      to generate the complete file.
                    </p>
                  </div>
                </div>
              </div>
            </Card>}
          {/* Export History */}
          {selectedTab === 'history' && <Card data-id="element-822">
              <div className="flex justify-between items-center mb-4" data-id="element-823">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-824">
                  Export History
                </h3>
                <div className="flex items-center" data-id="element-825">
                  <input type="text" placeholder="Search exports..." className="border border-gray-300 rounded-md px-3 py-1.5 mr-2" data-id="element-826" />
                  <select className="border border-gray-300 rounded-md px-3 py-1.5" data-id="element-827">
                    <option data-id="element-828">All Templates</option>
                    <option data-id="element-829">DBRS</option>
                    <option data-id="element-830">REIT</option>
                    <option data-id="element-831">Servicer</option>
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-832">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-833">
                  <thead className="bg-gray-50" data-id="element-834">
                    <tr data-id="element-835">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-836">
                        Export Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-837">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-838">
                        Format
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-839">
                        Counterparty
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-840">
                        User
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-841">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-842">
                    {mockExportHistory.map(export_ => <tr key={export_.id} className="hover:bg-gray-50" data-id="element-843">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-844">
                          <div className="flex items-center" data-id="element-845">
                            {export_.format === 'xlsx' && <TableIcon className="w-4 h-4 text-green-600 mr-2" data-id="element-846" />}
                            {export_.format === 'csv' && <FileTextIcon className="w-4 h-4 text-blue-600 mr-2" data-id="element-847" />}
                            {export_.format === 'json' && <DatabaseIcon className="w-4 h-4 text-amber-600 mr-2" data-id="element-848" />}
                            {export_.format === 'pdf' && <FileIcon className="w-4 h-4 text-red-600 mr-2" data-id="element-849" />}
                            <div data-id="element-850">
                              <div className="text-sm font-medium text-gray-900" data-id="element-851">
                                {export_.name}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-852">
                                Version {export_.version}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-853">
                          <div className="flex items-center" data-id="element-854">
                            <ClockIcon className="w-4 h-4 mr-1.5 text-gray-400" data-id="element-855" />
                            {export_.date}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-856">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full uppercase bg-gray-100 text-gray-800" data-id="element-857">
                            {export_.format}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-858">
                          {export_.counterparty}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-859">
                          {export_.user}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-860">
                          <div className="flex space-x-3" data-id="element-861">
                            <button className="text-blue-600 hover:text-blue-800" data-id="element-862">
                              <DownloadIcon className="w-4 h-4" data-id="element-863" />
                            </button>
                            <button className="text-blue-600 hover:text-blue-800" data-id="element-864">
                              <RefreshCwIcon className="w-4 h-4" data-id="element-865" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </Card>}
          {/* Actions Panel */}
          <Card data-id="element-866">
            <div className="flex flex-wrap gap-3" data-id="element-867">
              <button className="px-4 py-2 bg-blue-600 rounded-md text-white hover:bg-blue-700 flex items-center" onClick={handleRunExport} data-id="element-868">
                <DownloadIcon className="w-4 h-4 mr-2" data-id="element-869" />
                Run Export
              </button>
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-870">
                <RefreshCwIcon className="w-4 h-4 mr-2" data-id="element-871" />
                Reconfigure Fields
              </button>
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center" onClick={handleSubmitToBuyer} data-id="element-872">
                <SendIcon className="w-4 h-4 mr-2" data-id="element-873" />
                Submit to Buyer
              </button>
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center" onClick={handleSaveTemplate} data-id="element-874">
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-875" />
                Save Template
              </button>
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-876">
                <ClockIcon className="w-4 h-4 mr-2" data-id="element-877" />
                View Past Exports
              </button>
            </div>
          </Card>
        </div>
        {/* Template Metadata Sidebar */}
        {showTemplateMetadata && <div className="lg:col-span-1" data-id="element-878">
            <Card data-id="element-879">
              <div className="flex justify-between items-center mb-4" data-id="element-880">
                <h3 className="text-lg font-medium text-gray-800" data-id="element-881">
                  Template Metadata
                </h3>
                <button className="text-gray-400 hover:text-gray-600" data-id="element-882">
                  <XIcon className="w-4 h-4" onClick={() => setShowTemplateMetadata(false)} data-id="element-883" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-884">
                <div data-id="element-885">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-886">
                    Template Details
                  </h4>
                  <div className="space-y-2" data-id="element-887">
                    <div className="flex justify-between" data-id="element-888">
                      <span className="text-sm text-gray-600" data-id="element-889">Created</span>
                      <span className="text-sm text-gray-900" data-id="element-890">
                        Oct 22, 2025
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-891">
                      <span className="text-sm text-gray-600" data-id="element-892">Created By</span>
                      <span className="text-sm text-gray-900" data-id="element-893">
                        Jeff Martinez
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-894">
                      <span className="text-sm text-gray-600" data-id="element-895">
                        Last Modified
                      </span>
                      <span className="text-sm text-gray-900" data-id="element-896">
                        Oct 30, 2025
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-897">
                      <span className="text-sm text-gray-600" data-id="element-898">Version</span>
                      <span className="text-sm text-gray-900" data-id="element-899">1.3</span>
                    </div>
                  </div>
                </div>
                <div className="pt-3 border-t border-gray-200" data-id="element-900">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-901">
                    Export Summary
                  </h4>
                  <div className="space-y-2" data-id="element-902">
                    <div className="flex justify-between" data-id="element-903">
                      <span className="text-sm text-gray-600" data-id="element-904">Pool</span>
                      <span className="text-sm text-gray-900" data-id="element-905">
                        {activeTemplate.poolName}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-906">
                      <span className="text-sm text-gray-600" data-id="element-907">Target</span>
                      <span className="text-sm text-gray-900" data-id="element-908">
                        {activeTemplate.counterparty}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-909">
                      <span className="text-sm text-gray-600" data-id="element-910">
                        Fields Mapped
                      </span>
                      <span className="text-sm text-gray-900" data-id="element-911">
                        {activeTemplate.fields.length}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-912">
                      <span className="text-sm text-gray-600" data-id="element-913">Exceptions</span>
                      <span className="text-sm text-gray-900" data-id="element-914">
                        {activeTemplate.fields.filter(f => f.status === 'warning').length}{' '}
                        warning,{' '}
                        {activeTemplate.fields.filter(f => f.status === 'missing').length}{' '}
                        missing
                      </span>
                    </div>
                  </div>
                </div>
                <div className="pt-3 border-t border-gray-200" data-id="element-915">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-916">
                    Data Validation
                  </h4>
                  {activeTemplate.fields.filter(f => f.status !== 'ready').map(field => <div key={field.id} className={`p-2 rounded-md mb-2 ${field.status === 'warning' ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`} data-id="element-917">
                        <div className="flex items-start" data-id="element-918">
                          {field.status === 'warning' ? <AlertTriangleIcon className="w-4 h-4 text-amber-500 mr-1.5 mt-0.5 flex-shrink-0" data-id="element-919" /> : <XIcon className="w-4 h-4 text-red-500 mr-1.5 mt-0.5 flex-shrink-0" data-id="element-920" />}
                          <div data-id="element-921">
                            <p className={`text-xs font-medium ${field.status === 'warning' ? 'text-amber-800' : 'text-red-800'}`} data-id="element-922">
                              {field.exportName}
                            </p>
                            <p className={`text-xs ${field.status === 'warning' ? 'text-amber-700' : 'text-red-700'} mt-0.5`} data-id="element-923">
                              {field.status === 'warning' ? field.warning : 'Required field is missing'}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  {activeTemplate.fields.every(f => f.status === 'ready') && <div className="p-2 rounded-md mb-2 bg-green-50 border border-green-200" data-id="element-924">
                      <div className="flex items-start" data-id="element-925">
                        <CheckIcon className="w-4 h-4 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" data-id="element-926" />
                        <div data-id="element-927">
                          <p className="text-xs font-medium text-green-800" data-id="element-928">
                            All fields validated
                          </p>
                          <p className="text-xs text-green-700 mt-0.5" data-id="element-929">
                            Export is ready to run
                          </p>
                        </div>
                      </div>
                    </div>}
                </div>
                <div className="pt-3 border-t border-gray-200" data-id="element-930">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-931">
                    Related Templates
                  </h4>
                  <div className="space-y-2" data-id="element-932">
                    {mockTemplates.filter(t => t.id !== activeTemplate.id).slice(0, 3).map(template => <button key={template.id} className="w-full text-left p-2 hover:bg-gray-50 rounded-md flex items-center justify-between" onClick={() => setActiveTemplate(template)} data-id="element-933">
                          <div className="flex items-center" data-id="element-934">
                            <FileTextIcon className="w-4 h-4 text-gray-400 mr-2" data-id="element-935" />
                            <span className="text-sm text-gray-700" data-id="element-936">
                              {template.name}
                            </span>
                          </div>
                          <ChevronRightIcon className="w-4 h-4 text-gray-400" data-id="element-937" />
                        </button>)}
                  </div>
                </div>
              </div>
            </Card>
          </div>}
      </div>
      {/* Field Mapping Modal */}
      {showFieldMapping && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto" data-id="element-938">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-auto my-4" data-id="element-939">
            <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-940">
              <h2 className="text-lg font-semibold text-gray-800" data-id="element-941">
                Add Field Mapping
              </h2>
              <button onClick={handleCloseFieldMapping} className="text-gray-500 hover:text-gray-700" data-id="element-942">
                <XIcon className="w-5 h-5" data-id="element-943" />
              </button>
            </div>
            <div className="p-4" data-id="element-944">
              <div className="space-y-4" data-id="element-945">
                <div data-id="element-946">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-947">
                    Export Field Name
                  </label>
                  <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="e.g., Loan_ID, Borrower_Name" data-id="element-948" />
                </div>
                <div data-id="element-949">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-950">
                    Source Field
                  </label>
                  <div className="relative" data-id="element-951">
                    <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 pl-10" placeholder="Select or type field path..." data-id="element-952" />
                    <DatabaseIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-953" />
                  </div>
                  <div className="mt-1 text-xs text-gray-500 flex items-center" data-id="element-954">
                    <InfoIcon className="w-3.5 h-3.5 mr-1 text-blue-500" data-id="element-955" />
                    Use dot notation to access nested fields (e.g.,
                    loan.borrower.firstName)
                  </div>
                </div>
                <div data-id="element-956">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-957">
                    Transformation (Optional)
                  </label>
                  <select className="w-full border border-gray-300 rounded-md px-3 py-2" data-id="element-958">
                    <option value="" data-id="element-959">No transformation</option>
                    <option value="mask" data-id="element-960">Mask PII data</option>
                    <option value="round" data-id="element-961">Round to nearest value</option>
                    <option value="format" data-id="element-962">Format as percentage</option>
                    <option value="decimal" data-id="element-963">Fix decimal places</option>
                    <option value="custom" data-id="element-964">Custom transformation...</option>
                  </select>
                </div>
                <div data-id="element-965">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-966">
                    Field Requirements
                  </label>
                  <div className="flex items-start space-x-2" data-id="element-967">
                    <label className="flex items-center text-sm text-gray-700" data-id="element-968">
                      <input type="checkbox" className="mr-2 h-4 w-4 text-blue-600 rounded" data-id="element-969" />
                      Required Field
                    </label>
                    <label className="flex items-center text-sm text-gray-700" data-id="element-970">
                      <input type="checkbox" className="mr-2 h-4 w-4 text-blue-600 rounded" data-id="element-971" />
                      Validate Format
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg" data-id="element-972">
              <button className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 mr-2" onClick={handleCloseFieldMapping} data-id="element-973">
                Cancel
              </button>
              <button className="px-3 py-1.5 bg-blue-600 rounded-md text-sm text-white hover:bg-blue-700" onClick={handleCloseFieldMapping} data-id="element-974">
                Add Field
              </button>
            </div>
          </div>
        </div>}
    </div>;
}
// Mock data for templates
const mockTemplates: ExportTemplate[] = [{
  id: '1',
  name: 'Q3 Rental Flow - DBRS',
  counterparty: 'DBRS Morningstar',
  poolName: 'Rental Portfolio A',
  format: 'xlsx',
  deliveryMethod: 'download',
  versioningEnabled: true,
  fields: [{
    id: '1',
    exportName: 'Loan_ID',
    sourceField: 'loan.id',
    transformation: null,
    status: 'ready'
  }, {
    id: '2',
    exportName: 'Borrower Name',
    sourceField: 'borrower.fullName',
    transformation: 'Mask Last Name',
    status: 'warning',
    warning: 'PII data - masking applied'
  }, {
    id: '3',
    exportName: 'Property Value',
    sourceField: 'property.value',
    transformation: 'Rounded to $1,000',
    status: 'ready'
  }, {
    id: '4',
    exportName: 'FICO Score',
    sourceField: 'fico.score',
    transformation: null,
    status: 'ready'
  }, {
    id: '5',
    exportName: 'LTV (%)',
    sourceField: 'loan.ltv',
    transformation: '% Format',
    status: 'ready'
  }, {
    id: '6',
    exportName: 'DSCR',
    sourceField: 'underwriting.dscr',
    transformation: '2 decimal places',
    status: 'ready'
  }, {
    id: '7',
    exportName: 'Appraisal Date',
    sourceField: 'documents.appraisal.date',
    transformation: null,
    status: 'missing'
  }],
  createdBy: 'Jeff Martinez',
  createdDate: 'Oct 22, 2025',
  lastModified: 'Oct 30, 2025',
  targetSystem: 'DBRS'
}, {
  id: '2',
  name: 'REIT Monthly Report',
  counterparty: 'Blackstone REIT',
  poolName: 'Q4 DSCR Portfolio',
  format: 'xlsx',
  deliveryMethod: 'sftp',
  versioningEnabled: true,
  fields: [{
    id: '1',
    exportName: 'Loan_ID',
    sourceField: 'loan.id',
    transformation: null,
    status: 'ready'
  }, {
    id: '2',
    exportName: 'Borrower_Name',
    sourceField: 'borrower.fullName',
    transformation: null,
    status: 'ready'
  }, {
    id: '3',
    exportName: 'Property_Value',
    sourceField: 'property.value',
    transformation: null,
    status: 'ready'
  }],
  createdBy: 'Maria Lopez',
  createdDate: 'Oct 15, 2025',
  lastModified: 'Oct 29, 2025',
  targetSystem: 'REIT'
}, {
  id: '3',
  name: 'Servicer Onboarding',
  counterparty: 'SLS Servicing',
  poolName: 'Bridge Loan Package',
  format: 'csv',
  deliveryMethod: 'email',
  versioningEnabled: false,
  fields: [{
    id: '1',
    exportName: 'Loan_ID',
    sourceField: 'loan.id',
    transformation: null,
    status: 'ready'
  }, {
    id: '2',
    exportName: 'Borrower_Name',
    sourceField: 'borrower.fullName',
    transformation: null,
    status: 'ready'
  }],
  createdBy: 'Robert Johnson',
  createdDate: 'Oct 10, 2025',
  lastModified: 'Oct 25, 2025',
  targetSystem: 'Servicer'
}];
// Mock data for export history
const mockExportHistory = [{
  id: '1',
  name: 'Q3 Rental Flow - DBRS',
  date: 'Oct 30, 2025',
  format: 'xlsx',
  counterparty: 'DBRS Morningstar',
  user: 'Jeff Martinez',
  version: '1.3'
}, {
  id: '2',
  name: 'Q3 Rental Flow - DBRS',
  date: 'Oct 28, 2025',
  format: 'xlsx',
  counterparty: 'DBRS Morningstar',
  user: 'Jeff Martinez',
  version: '1.2'
}, {
  id: '3',
  name: 'REIT Monthly Report',
  date: 'Oct 25, 2025',
  format: 'xlsx',
  counterparty: 'Blackstone REIT',
  user: 'Maria Lopez',
  version: '2.0'
}, {
  id: '4',
  name: 'Servicer Onboarding',
  date: 'Oct 20, 2025',
  format: 'csv',
  counterparty: 'SLS Servicing',
  user: 'Robert Johnson',
  version: '1.0'
}, {
  id: '5',
  name: 'Q3 Rental Flow - DBRS',
  date: 'Oct 15, 2025',
  format: 'xlsx',
  counterparty: 'DBRS Morningstar',
  user: 'Jeff Martinez',
  version: '1.1'
}];